import dayjs from "dayjs";

import type {
  CreateMessageTemplateInput,
  MessageTemplate,
  MessageTemplateHeaderFormat,
} from "../types";
import {
  DEFAULT_WHATSAPP_TEMPLATE_CATEGORY,
  DEFAULT_WHATSAPP_TEMPLATE_LANGUAGE,
  extractUniqueNamedVariables,
  normalizeTemplateInput,
  validateBodyText,
  validateButtonLabel,
  validateButtonUrl,
  validateHeaderMediaFile,
  validateHeaderText,
  validateTemplateName,
  type HeaderMediaFormat,
} from "../validation/messageTemplateValidation";

type WhatsAppTextFormat = "TEXT" | "IMAGE" | "VIDEO" | "DOCUMENT";

type WhatsAppNamedParamExample = {
  param_name: string;
  example: string;
};

type WhatsAppTemplateComponentExample = {
  body_text_named_params?: WhatsAppNamedParamExample[];
  header_text_named_params?: WhatsAppNamedParamExample[];
  header_handle?: string[];
};

type WhatsAppTemplateComponent = {
  type: "BODY" | "HEADER" | "BUTTONS";
  /**
   * A API aceita `format` para HEADER textual ou de mídia.
   * Para BODY, no fluxo atual, enviamos sem `format` para manter
   * compatibilidade com o payload legado esperado.
   */
  format?: WhatsAppTextFormat;
  text?: string;
  example?: WhatsAppTemplateComponentExample;
  buttons?: {
    type: "URL";
    text: string;
    url?: string;
    phone_number?: string;
  }[];
};

type WhatsAppTemplatePayload = {
  name: string;
  language: string;
  category: "MARKETING";
  components: WhatsAppTemplateComponent[];
  parameter_format?: "named";
};

type RawWhatsAppTemplate = {
  id?: unknown;
  template_id?: unknown;
  name?: unknown;
  language?: unknown;
  category?: unknown;
  status?: unknown;
  created_at?: unknown;
  createdAt?: unknown;
  components?: unknown;
};

const DEFAULT_VARIABLE_EXAMPLE = "Exemplo";

const VARIABLE_EXAMPLE_BY_NAME: Record<string, string> = {
  nome_paciente: "João Silva",
  nome_clinica: "Clínica Veterinária",
  data_consulta: "20/03/2026",
  hora_consulta: "14:30",
  nome_profissional: "Dr. Ana Silva",
  especialidade: "Clínica geral",
  endereco_consulta: "Rua Exemplo, 100",
  link_teleconsulta: "https://meet.exemplo.com/sala",
  motivo_cancelamento: "Indisponibilidade de agenda",
  valor_consulta: "R$ 150,00",
  data_vencimento: "25/03/2026",
  link_pagamento: "https://pagamento.exemplo.com/abc",
  nome_medicamento: "Dipirona 500mg",
  posologia: "1 comprimido a cada 8 horas",
  observacao: "Tomar após as refeições",
  nome_exame: "Hemograma completo",
  data_exame: "22/03/2026",
  orientacao: "Jejum de 8 horas",
  link_resultado: "https://resultados.exemplo.com/abc",
  questionnaire_link: "https://fluxmed.com/questionario/abc123",
};

const MEDIA_HEADER_FORMATS = new Set<MessageTemplateHeaderFormat>([
  "IMAGE",
  "VIDEO",
  "DOCUMENT",
]);

function toIsoString(value: unknown): string | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    const d = dayjs(value);
    return d.isValid() ? d.toISOString() : null;
  }

  if (typeof value === "string") {
    const d = dayjs(value);
    return d.isValid() ? d.toISOString() : null;
  }

  return null;
}

function buildNamedParamsExample(
  variableNames: string[],
): WhatsAppNamedParamExample[] {
  return variableNames.map((param_name) => ({
    param_name,
    example: VARIABLE_EXAMPLE_BY_NAME[param_name] ?? DEFAULT_VARIABLE_EXAMPLE,
  }));
}

function resolveHeaderFormat(
  input: CreateMessageTemplateInput | MessageTemplate,
): MessageTemplateHeaderFormat | null {
  const explicit = input.headerFormat;
  if (explicit === "IMAGE" || explicit === "VIDEO" || explicit === "DOCUMENT") {
    return explicit;
  }
  if (explicit === "TEXT") {
    const text = String(input.header ?? "").trim();
    return text ? "TEXT" : null;
  }
  const text = String(input.header ?? "").trim();
  return text ? "TEXT" : null;
}

function isLooksLikeUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

export function buildWhatsAppTemplatePayloadFromUiInput(
  input: CreateMessageTemplateInput | MessageTemplate,
  params?: {
    language?: string;
    category?: WhatsAppTemplatePayload["category"];
    /**
     * Handle já resolvido (após upload). Tem prioridade sobre `input.headerMediaHandle`.
     */
    resolvedHeaderMediaHandle?: string;
  },
): WhatsAppTemplatePayload {
  const language = params?.language ?? DEFAULT_WHATSAPP_TEMPLATE_LANGUAGE;
  const category = params?.category ?? DEFAULT_WHATSAPP_TEMPLATE_CATEGORY;

  if (language !== DEFAULT_WHATSAPP_TEMPLATE_LANGUAGE) {
    throw new Error(
      `Idioma inválido. Use "${DEFAULT_WHATSAPP_TEMPLATE_LANGUAGE}".`,
    );
  }

  if (category !== DEFAULT_WHATSAPP_TEMPLATE_CATEGORY) {
    throw new Error(
      `Categoria inválida. Use "${DEFAULT_WHATSAPP_TEMPLATE_CATEGORY}".`,
    );
  }

  const headerFormat = resolveHeaderFormat(input);
  const isMediaHeader =
    headerFormat !== null && MEDIA_HEADER_FORMATS.has(headerFormat);

  const normalizedInput = normalizeTemplateInput({
    name: input.name,
    header: isMediaHeader ? "" : input.header,
    body: input.preview,
  });

  const nameValidation = validateTemplateName(normalizedInput.name);
  if (!nameValidation.valid) {
    throw new Error(nameValidation.error);
  }

  const bodyText = normalizedInput.body;
  const bodyValidation = validateBodyText(bodyText);
  if (!bodyValidation.valid) {
    throw new Error(bodyValidation.error);
  }

  const bodyVariables = extractUniqueNamedVariables(bodyText);
  const components: WhatsAppTemplateComponent[] = [];
  let headerVariables: string[] = [];

  if (isMediaHeader && headerFormat) {
    const mediaHandle = String(
      params?.resolvedHeaderMediaHandle ?? input.headerMediaHandle ?? "",
    ).trim();
    if (!mediaHandle) {
      throw new Error(
        "Handle da mídia do cabeçalho é obrigatório para IMAGE/VIDEO/DOCUMENT.",
      );
    }

    const fileForValidation =
      "headerMediaFile" in input ? input.headerMediaFile : null;
    if (fileForValidation) {
      const mediaValidation = validateHeaderMediaFile(
        headerFormat as HeaderMediaFormat,
        fileForValidation,
        mediaHandle,
      );
      if (!mediaValidation.valid) {
        throw new Error(mediaValidation.error);
      }
    }

    components.push({
      type: "HEADER",
      format: headerFormat,
      example: {
        header_handle: [mediaHandle],
      },
    });
  } else if (headerFormat === "TEXT") {
    const headerText = normalizedInput.header;
    const headerValidation = validateHeaderText(headerText);
    if (!headerValidation.valid) {
      throw new Error(headerValidation.error);
    }

    headerVariables = headerText
      ? extractUniqueNamedVariables(headerText)
      : [];

    if (headerText) {
      const headerComponent: WhatsAppTemplateComponent = {
        type: "HEADER",
        format: "TEXT",
        text: headerText,
        ...(headerVariables.length > 0
          ? {
              example: {
                header_text_named_params:
                  buildNamedParamsExample(headerVariables),
              },
            }
          : {}),
      };
      components.push(headerComponent);
    }
  }

  components.push({
    type: "BODY",
    text: bodyText,
    ...(bodyVariables.length > 0
      ? {
          example: {
            body_text_named_params: buildNamedParamsExample(bodyVariables),
          },
        }
      : {}),
  });

  if (input.buttonType && input.buttonText) {
    const buttonText = input.buttonText.trim();
    const buttonValue = (input.buttonValue ?? "").trim();

    const labelValidation = validateButtonLabel(buttonText);
    if (!labelValidation.valid) throw new Error(labelValidation.error);

    if (input.buttonType !== "URL") {
      throw new Error("Valor do botão é obrigatório para URL.");
    }

    const urlValidation = validateButtonUrl(buttonValue);
    if (!urlValidation.valid) throw new Error(urlValidation.error);

    components.push({
      type: "BUTTONS",
      buttons: [
        {
          type: input.buttonType,
          text: buttonText,
          url: buttonValue,
        },
      ],
    });
  }

  const hasNamedParams = headerVariables.length > 0 || bodyVariables.length > 0;

  const payload: WhatsAppTemplatePayload = {
    name: normalizedInput.name,
    language,
    category,
    components,
    ...(hasNamedParams ? { parameter_format: "named" as const } : {}),
  };

  return payload;
}

function parseHeaderFormat(raw: unknown): MessageTemplateHeaderFormat | undefined {
  const value = String(raw ?? "").toUpperCase();
  if (
    value === "TEXT" ||
    value === "IMAGE" ||
    value === "VIDEO" ||
    value === "DOCUMENT"
  ) {
    return value;
  }
  return undefined;
}

function extractHeaderHandle(example: unknown): string {
  if (!example || typeof example !== "object") return "";
  const handleList = (example as Record<string, unknown>).header_handle;
  if (!Array.isArray(handleList) || handleList.length === 0) return "";
  const first = handleList[0];
  return typeof first === "string" ? first.trim() : "";
}

export function mapRawWhatsAppTemplateToUiTemplate(
  raw: RawWhatsAppTemplate,
): MessageTemplate {
  const id = String(raw.id ?? raw.template_id ?? raw.name ?? "");
  const name = String(raw.name ?? "");
  const status = String(raw.status ?? "");

  const components = Array.isArray(raw.components)
    ? (raw.components as any[])
    : [];
  const headerComponent = components.find((c) => c?.type === "HEADER") as any;
  const bodyComponent = components.find((c) => c?.type === "BODY") as any;
  const buttonComponent = components.find((c) => c?.type === "BUTTONS") as any;
  const firstButton = Array.isArray(buttonComponent?.buttons)
    ? buttonComponent.buttons[0]
    : null;

  const headerFormat =
    parseHeaderFormat(headerComponent?.format) ??
    (headerComponent?.text ? "TEXT" : undefined);

  const isMediaHeader =
    headerFormat === "IMAGE" ||
    headerFormat === "VIDEO" ||
    headerFormat === "DOCUMENT";

  const headerMediaHandle = isMediaHeader
    ? extractHeaderHandle(headerComponent?.example)
    : undefined;
  const headerMediaUrl =
    headerMediaHandle && isLooksLikeUrl(headerMediaHandle)
      ? headerMediaHandle
      : undefined;

  const header = isMediaHeader ? "" : String(headerComponent?.text ?? "");
  const preview = String(bodyComponent?.text ?? "");

  const createdAtIso =
    toIsoString(raw.created_at) ??
    toIsoString(raw.createdAt) ??
    dayjs().toISOString();

  return {
    id,
    name,
    header,
    ...(headerFormat ? { headerFormat } : {}),
    ...(headerMediaHandle ? { headerMediaHandle } : {}),
    ...(headerMediaUrl ? { headerMediaUrl } : {}),
    preview,
    status,
    buttonType: firstButton?.type,
    buttonText: String(firstButton?.text ?? ""),
    buttonValue:
      String(firstButton?.url ?? firstButton?.phone_number ?? ""),
    createdAtIso,
  };
}
